import React, { useState } from "react";
import {
  X,
  Globe,
  Moon,
  Sun,
  LogOut,
  ShieldAlert,
  ExternalLink,
} from "lucide-react";
import { doc, setDoc, deleteDoc } from "firebase/firestore";
import { signOut, deleteUser } from "firebase/auth";
import { db, auth } from "../firebase";
import { useAppContext } from "../context/AppContext";

interface ProfileSettingsProps {
  setShowSettings: (v: boolean) => void;
  ownerName: string;
  setOwnerName: (v: string) => void;
  ownerPhone: string;
  setOwnerPhone: (v: string) => void;
}

export default function ProfileSettings({
  setShowSettings,
  ownerName,
  setOwnerName,
  ownerPhone,
  setOwnerPhone,
}: ProfileSettingsProps) {
  const { lang, setLang, t, isDark, setIsDark, user } = useAppContext();
  const [groqKey, setGroqKey] = useState(
    localStorage.getItem("mypaw_gemini_key") || ""
  );

  const saveOwnerSettings = async () => {
    if (!user) return;
    localStorage.setItem("mypaw_gemini_key", groqKey.trim());
    await setDoc(
      doc(db, "users", user.uid),
      { ownerName, ownerPhone },
      { merge: true }
    );
    setShowSettings(false);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    const confirmMsg =
      lang === "uk"
        ? "Ви впевнені, що хочете НАЗАВЖДИ видалити свій акаунт та всі дані? Цю дію неможливо скасувати."
        : "Are you sure you want to PERMANENTLY delete your account and all data? This cannot be undone.";

    if (window.confirm(confirmMsg)) {
      try {
        await deleteDoc(doc(db, "users", user.uid));
        await deleteUser(user);
      } catch (error: any) {
        if (error.code === "auth/requires-recent-login") {
          alert(
            lang === "uk"
              ? "З метою безпеки, будь ласка, вийдіть з акаунту, увійдіть знову і повторіть спробу видалення."
              : "For security reasons, please log out, log back in, and try again."
          );
          signOut(auth);
        } else {
          alert(
            lang === "uk"
              ? "Сталася помилка. Спробуйте пізніше."
              : "An error occurred."
          );
        }
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[300] flex flex-col justify-end backdrop-blur-sm animate-fade-in"
      onClick={() => setShowSettings(false)}
    >
      <div
        className="bg-[var(--bg-card)] rounded-t-[2.5rem] p-6 pb-12 animate-slide-up border-t border-[var(--border-color)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-extrabold text-xl text-[var(--text-main)]">
            Налаштування
          </h3>
          <button
            onClick={() => setShowSettings(false)}
            className="text-[var(--text-main)] active:scale-95"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col gap-4 mb-6">
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] uppercase mb-2 block">
              Ім'я власника
            </label>
            <input
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              className="w-full h-12 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm"
              placeholder="Ваше ім'я"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] uppercase mb-2 block">
              Телефон (для QR)
            </label>
            <input
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
              className="w-full h-12 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm"
              placeholder="+380..."
            />
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] uppercase mb-2 block">
              AI API Key (Groq)
            </label>
            <input
              type="password"
              value={groqKey}
              onChange={(e) => setGroqKey(e.target.value)}
              className="w-full h-12 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm"
              placeholder="gsk_..."
            />
          </div>
          <button
            onClick={saveOwnerSettings}
            className="w-full py-3 bg-[#a073e7] text-white font-bold rounded-xl active:scale-95 shadow-sm text-sm mt-2"
          >
            Зберегти дані
          </button>
        </div>

        <div className="flex flex-col gap-3 border-t border-[var(--border-color)] pt-6">
          <button
            onClick={() => setLang(lang === "uk" ? "en" : "uk")}
            className="w-full py-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] font-bold rounded-xl flex items-center justify-between px-5 active:scale-95 shadow-sm"
          >
            <span className="flex items-center gap-3">
              <Globe size={20} /> {lang === "uk" ? "Мова" : "Language"}
            </span>
            <span className="text-[#a073e7] bg-[var(--accent-soft)] px-3 py-1 rounded-md">
              {lang === "uk" ? "Українська" : "English"}
            </span>
          </button>
          <button
            onClick={() => setIsDark(!isDark)}
            className="w-full py-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] font-bold rounded-xl flex items-center justify-between px-5 active:scale-95 shadow-sm"
          >
            <span className="flex items-center gap-3">
              {isDark ? <Moon size={20} /> : <Sun size={20} />} Dark Mode
            </span>
            <div
              className={`w-12 h-7 rounded-full flex items-center p-1 shadow-inner ${
                isDark ? "bg-[#a073e7]" : "bg-[var(--border-color)]"
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  isDark ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </div>
          </button>
          <button
            onClick={() =>
              window.open(
                "https://docs.google.com/document/d/1vpwxlLMFSKlBqzBXYF3nMWeg75xrg8BogaadEpmrx5Q/edit?usp=sharing",
                "_blank"
              )
            }
            className="w-full py-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] font-bold rounded-xl flex items-center justify-between px-5 active:scale-95 shadow-sm"
          >
            <span className="flex items-center gap-3">
              <ShieldAlert size={20} className="text-[#10b981]" />{" "}
              {lang === "uk" ? "Політика конфіденційності" : "Privacy Policy"}
            </span>
            <ExternalLink size={16} className="text-[var(--text-muted)]" />
          </button>
          <button
            onClick={() => signOut(auth)}
            className="w-full mt-4 py-4 bg-[var(--bg-card)] text-[var(--text-main)] font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 border border-[var(--border-color)] shadow-sm"
          >
            <LogOut size={20} /> {t.logout}
          </button>
          <button
            onClick={handleDeleteAccount}
            className="w-full mt-2 py-4 bg-[var(--red-soft)] text-[var(--red-text)] font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 border border-[var(--red-soft)] shadow-sm"
          >
            <ShieldAlert size={20} />{" "}
            {lang === "uk"
              ? "Видалити акаунт назавжди"
              : "Delete Account Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
